import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { listProductionOrders } from "../api/productionOrders";
import { listCardsPaged } from "../api/cards";
import { listMaterials } from "../api/materials";
import { createFasonOrder } from "../api/fasonOrders";
import ReferencePickerDialog from "./ReferencePickerDialog";

const OUTPUT_MATERIAL_IDS = [26, 45];

function FasonOrderModal({ onClose, onSaved }) {
  // Statik listeler
  const [inputMaterial, setInputMaterial] = useState(null);
  const [outputMaterials, setOutputMaterials] = useState([]);
  const [staticLoading, setStaticLoading] = useState(true);

  // Form alanları
  const [productionOrderId, setProductionOrderId] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [productionOrderLabel, setProductionOrderLabel] = useState("");
  const [supplierLabel, setSupplierLabel] = useState("");
  const [picker, setPicker] = useState(null);
  const [outputMaterialId, setOutputMaterialId] = useState("");
  const [sheetWidth, setSheetWidth] = useState("");
  const [sheetHeight, setSheetHeight] = useState("");
  const [qtyOrdered, setQtyOrdered] = useState("");
  const [platesPerSheet, setPlatesPerSheet] = useState("");
  const [orderDate, setOrderDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const qtyOrderedPlates =
    Number(qtyOrdered) > 0 && Number(platesPerSheet) > 0
      ? Math.round(Number(qtyOrdered) * Number(platesPerSheet))
      : null;

  // Statik veriler
  useEffect(() => {
    async function loadStatic() {
      try {
        const mats = await listMaterials({ limit: 500 });

        const allMats = Array.isArray(mats) ? mats : [];
        const rawMats = allMats.filter((m) => String(m.material_type || "").toLowerCase() === "raw");
        if (rawMats.length === 1) {
          setInputMaterial(rawMats[0]);
        } else if (rawMats.length > 1) {
          const tabaka = rawMats.find((m) => String(m.name || "").toLowerCase().includes("tabaka"));
          setInputMaterial(tabaka || rawMats[0]);
        }

        const outMats = allMats.filter((m) => OUTPUT_MATERIAL_IDS.includes(Number(m.id)));
        setOutputMaterials(outMats);
      } catch (err) {
        console.error("FasonOrderModal static load error:", err);
        setError("Veriler yüklenemedi");
      } finally {
        setStaticLoading(false);
      }
    }
    loadStatic();
  }, []);

  const loadFasonWorkOrders = useCallback(({ page, search }) => listProductionOrders({
    active_only: "1", manufacturing_source: "2", page, limit: 10, search,
  }), []);
  const loadSuppliers = useCallback(({ page, search }) => listCardsPaged({
    type: "supplier", page, limit: 10, search,
  }), []);

  const pickerConfig = picker === "productionOrder" ? {
    title: "İş Emri Seç", searchPlaceholder: "İş emri no veya ürün adı ile ara...", fetchPage: loadFasonWorkOrders,
    getPrimary: (item) => item.order_no, getSecondary: (item) => item.product_name || item.product_material_name || "İş emri",
  } : picker === "supplier" ? {
    title: "Tedarikçi Seç", searchPlaceholder: "Tedarikçi adı ile ara...", fetchPage: loadSuppliers,
    getPrimary: (item) => item.name, getSecondary: (item) => item.code || "Tedarikçi kartı",
  } : null;

  function selectReference(item) {
    if (picker === "productionOrder") {
      setProductionOrderId(String(item.id));
      setProductionOrderLabel(item.order_no);
    }
    if (picker === "supplier") {
      setSupplierId(String(item.id));
      setSupplierLabel(item.name);
    }
    setPicker(null);
  }

  async function save() {
    setError("");
    if (!productionOrderId) { setError("İş emri seçilmedi"); return; }
    if (!supplierId) { setError("Tedarikçi seçilmedi"); return; }
    if (!inputMaterial?.id) { setError("Giriş hammaddesi bulunamadı"); return; }
    if (!outputMaterialId) { setError("Çıktı yarımamül seçilmedi"); return; }
    const qty = Number(qtyOrdered);
    if (!Number.isFinite(qty) || qty <= 0) { setError("Sipariş miktarı > 0 olmalı"); return; }
    const pps = platesPerSheet ? Number(platesPerSheet) : null;
    if (pps !== null && (!Number.isFinite(pps) || pps <= 0)) {
      setError("Bıçak tabak sayısı > 0 olmalı"); return;
    }

    const sw = sheetWidth !== "" && Number.isFinite(Number(sheetWidth)) ? Number(sheetWidth) : null;
    const sh = sheetHeight !== "" && Number.isFinite(Number(sheetHeight)) ? Number(sheetHeight) : null;

    const payload = {
      production_order_id: Number(productionOrderId),
      supplier_id: Number(supplierId),
      input_material_id: Number(inputMaterial.id),
      output_material_id: Number(outputMaterialId),
      qty_ordered: qty,
      plates_per_sheet: pps,
      sheet_width: sw,
      sheet_height: sh,
      order_date: orderDate || null,
      notes: notes || null,
    };

    setSaving(true);
    try {
      await createFasonOrder(payload);
      await onSaved?.();
      onClose?.();
    } catch (err) {
      console.error("FasonOrderModal save error:", err);
      const msg =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        err?.message ||
        "Kaydedilemedi";
      setError(msg);
    } finally {
      setSaving(false);
    }
  }

  const isLoading = staticLoading;

  return (
    <>
    <Dialog open onClose={saving ? undefined : onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 4, overflow: "hidden", boxShadow: "0 24px 70px rgba(15, 23, 42, 0.22)" } }}>
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{ px: { xs: 2.5, sm: 3.5 }, py: 2.5, color: "#fff", background: "linear-gradient(135deg, #312e81 0%, #4f46e5 56%, #6366f1 100%)" }}>
          <Stack direction="row" alignItems="center" spacing={1.5}><Box sx={{ width: 42, height: 42, borderRadius: 2.25, display: "grid", placeItems: "center", bgcolor: "rgba(255,255,255,0.16)", border: "1px solid rgba(255,255,255,0.24)", fontSize: 19 }}>↗</Box><Box><Typography variant="h6" fontWeight={800}>Yeni Fason Siparişi</Typography><Typography variant="body2" sx={{ opacity: 0.8 }}>Sipariş numarası otomatik oluşturulur; üretim ve tedarik bilgilerini belirleyin.</Typography></Box></Stack>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: "#f7f8fc" }}>
        {error ? <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert> : null}
        <Stack spacing={2} sx={{
          "& .MuiOutlinedInput-root": { borderRadius: 2.25, bgcolor: "#fff" },
          "& > .MuiTextField-root, & > .MuiBox-root": { border: "1px solid #e7eaf3", borderRadius: 3, bgcolor: "#fff" },
        }}>

          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: "1px solid #e7eaf3", bgcolor: "#fff" }}>
            <Typography fontWeight={800}>İş emri</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>Fason üretim kaynağına sahip açık iş emrini seçin.</Typography>
            <Button variant="outlined" fullWidth onClick={() => setPicker("productionOrder")} disabled={saving} sx={{ justifyContent: "flex-start", textTransform: "none", borderRadius: 2, color: productionOrderId ? "#3730a3" : "text.secondary", fontWeight: productionOrderId ? 700 : 400 }}>
              {productionOrderId ? productionOrderLabel : "İş emri seçiniz"}
            </Button>
          </Paper>

          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: "1px solid #e7eaf3", bgcolor: "#fff" }}>
            <Typography fontWeight={800}>Tedarikçi (Basımevi)</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>Fason işi göndereceğiniz tedarikçiyi seçin.</Typography>
            <Button variant="outlined" fullWidth onClick={() => setPicker("supplier")} disabled={saving || staticLoading} sx={{ justifyContent: "flex-start", textTransform: "none", borderRadius: 2, color: supplierId ? "#3730a3" : "text.secondary", fontWeight: supplierId ? 700 : 400 }}>
              {supplierId ? supplierLabel : "Tedarikçi seçiniz"}
            </Button>
          </Paper>

          {/* Giriş hammaddesi — otomatik */}
          <TextField
            label="Giriş Hammaddesi (Hammadde Tabaka)"
            value={
              staticLoading ? "Yükleniyor..."
              : inputMaterial ? `${inputMaterial.code || ""} — ${inputMaterial.name || ""}`
              : "Hammadde tabaka bulunamadı"
            }
            InputProps={{ readOnly: true }}
            helperText="Otomatik seçilir"
          />

          {/* Çıktı yarımamül */}
          <TextField
            select label="Çıktı Yarımamül" value={outputMaterialId}
            onChange={(e) => setOutputMaterialId(e.target.value)}
            required disabled={saving || staticLoading}
            helperText="Baskıdan dönen yarımamül — varyant bilgisi iş emrinden otomatik belirlenir"
          >
            {outputMaterials.length === 0 && !staticLoading ? (
              <MenuItem value="" disabled>Yarımamül bulunamadı</MenuItem>
            ) : null}
            {outputMaterials.map((m) => (
              <MenuItem key={m.id} value={m.id}>{m.code} — {m.name}</MenuItem>
            ))}
          </TextField>

          {/* Tabaka Ölçüleri */}
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
              Hammadde Tabaka Ölçüleri
            </Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Tabaka En (cm)" type="number"
                inputProps={{ min: 0, step: 0.01 }}
                value={sheetWidth}
                onChange={(e) => setSheetWidth(e.target.value)}
                disabled={saving}
                helperText="Genişlik"
                sx={{ flex: 1 }}
              />
              <TextField
                label="Tabaka Boy (cm)" type="number"
                inputProps={{ min: 0, step: 0.01 }}
                value={sheetHeight}
                onChange={(e) => setSheetHeight(e.target.value)}
                disabled={saving}
                helperText="Uzunluk"
                sx={{ flex: 1 }}
              />
            </Stack>
          </Box>

          {/* Miktar & Bıçak */}
          <TextField
            label="Sipariş Miktarı (Tabaka)" type="number"
            inputProps={{ min: 1 }} value={qtyOrdered}
            onChange={(e) => setQtyOrdered(e.target.value)}
            required disabled={saving}
            helperText="Kaç tabaka sipariş verildi?"
          />

          <TextField
            label="Bıçak Tabak Sayısı (tabak/tabaka)" type="number"
            inputProps={{ min: 1 }} value={platesPerSheet}
            onChange={(e) => setPlatesPerSheet(e.target.value)}
            disabled={saving}
            helperText={
              qtyOrderedPlates != null
                ? `${qtyOrdered} tabaka × ${platesPerSheet} = ${qtyOrderedPlates.toLocaleString("tr-TR")} tabak`
                : "Opsiyonel — girince toplam tabak sayısı hesaplanır"
            }
          />

          <TextField
            label="Sipariş Tarihi" type="date" value={orderDate}
            onChange={(e) => setOrderDate(e.target.value)}
            InputLabelProps={{ shrink: true }} disabled={saving}
          />

          <TextField
            label="Not" multiline rows={2} value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={saving} helperText="Opsiyonel"
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, sm: 3.5 }, py: 2, bgcolor: "#fff", borderTop: "1px solid #e7eaf3", justifyContent: "flex-end", gap: 1 }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>İptal</Button>
        <Button onClick={save} variant="contained" disabled={saving || isLoading} sx={{ px: 2.25, borderRadius: 2, textTransform: "none", fontWeight: 800, bgcolor: "#4f46e5", "&:hover": { bgcolor: "#3730a3" } }}>
          {saving ? "Kaydediliyor..." : "Kaydet"}
        </Button>
      </DialogActions>
    </Dialog>
    {pickerConfig ? <ReferencePickerDialog open={Boolean(picker)} {...pickerConfig} onClose={() => setPicker(null)} onSelect={selectReference} /> : null}
    </>
  );
}

export default FasonOrderModal;
